import { css, unsafeCSS, html, dateToString, dateToTimeString } from 'omni-ui';
import * as htmlToImage from 'html-to-image';

import { CDN_DEPENDENCIES_BASE } from './constants.js';

/**
 * @namespace utils
 * @description Utils used by multiple components/modules
 */

/**
 * @memberof utils
 * @function
 * @param {string} bytes - The byte count to translate
 * @returns {string}
 */
export const bytesToMb = bytes => {
  const mb = bytes / 1024 ** 2;

  return bytes % 1024 ? mb.toFixed(1) : mb;
};

/**
 * @memberof utils
 * @function
 * @param {string} word - The word to capitalize
 * @returns {string}
 */
export const capitalizeWord = word =>
  `${word[0].toUpperCase()}${word.slice(1)}`;

/**
 * @memberof utils
 * @function
 * @param {string} date string (or object)
 * @returns {string}
 */
export const formatDate = (date, includeTime) => {
  // If the date is yyyy-mm-dd format, parse manually to avoid timezone issues
  const yyyymmdd = date.match?.(/^(\d{4})-(\d{2})-(\d{2})$/);
  const d = yyyymmdd
    ? new Date(yyyymmdd[1], yyyymmdd[2] - 1, yyyymmdd[3])
    : new Date(date);

  return includeTime
    ? `${dateToString(d)} ${dateToTimeString(d)}`
    : dateToString(d);
};

/**
 * @memberof utils
 * @function
 * @param {number} amount - amount of money
 * @param {string} currency - currency code (e.g. USD)
 * @param {number} minimumFractionDigits - passed to toLocaleString(), default 0
 * @param {number} maximumFractionDigits - passed to toLocaleString(), default 0
 * @returns {string}
 */
export const formatCurrency = (
  amount,
  currency,
  minimumFractionDigits = 0,
  maximumFractionDigits = 0,
) => {
  if (!currency) return String(amount ?? 0); // No currency => just use amount
  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
  });
};

/**
 * @memberof utils
 * @function
 * @description "sleep" function - suspends execution for input duration
 * @param {number} [duration=0]
 * @returns {Promise}
 */
export const wait = (duration = 0) =>
  new Promise(resolve => {
    setTimeout(resolve, duration);
  });

/**
 * @memberof utils
 * @function
 * @description previously from {@link https://gist.github.com/pinkhominid/742ff38688f7638d4eb795048b620e8e}
 * @param {array} paramCollections - rest argument from multiple paramters passed in
 * @returns {string} - from a `URLSearchParams(paramsObj).toString()`
 */
export const mergeURLSearchParams = (...paramCollections) => {
  let paramsObj;
  for (const collection of paramCollections) {
    paramsObj = {
      ...paramsObj,
      ...Object.fromEntries(new URLSearchParams(collection)),
    };
  }
  return new URLSearchParams(paramsObj).toString();
};

/**
 * @todo Is this stupid? You should just open spotlighht and type px/14
 * @memberof utils
 * @function
 * @description Calculate `rem` values based on base font size, given a number that represents the number of pixels
 * @param {number} px - Pixel value to convert to rem
 */
export const pixToRem = px => {
  const baseFont = Number(
    window
      .getComputedStyle(document.body)
      .getPropertyValue('font-size')
      .replace(/\D+/, ''),
  );
  const remValue = (px / baseFont).toFixed(2);

  return css`
    ${unsafeCSS(remValue)}rem
  `;
};

/**
 * @memberof utils
 * @function
 * @description Utility function to add a cookie with a particular name/value/expiration
 * @param {string} name - The cookie's name
 * @param {string} value - The cookie's value
 * @param {number} hours - The number of hours in the future to expire the cookie
 * @param {string} path - The path associated with the cookie
 */
export const setCookie = (name, value, hours = 12, path = '/') => {
  const expires = new Date(Date.now() + hours * 60 * 60 * 1000).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(
    value,
  )}; expires=${expires}; path=${path}`;
};

/**
 * @memberof utils
 * @function
 * @description Returns a cookie value given its name
 * @param {string} name - The cookie's name
 * @returns {string}
 */
export const getCookie = name =>
  document.cookie.split('; ').reduce((r, v) => {
    const parts = v.split('=');
    return parts[0] === name ? decodeURIComponent(parts[1]) : r;
  }, '');

/**
 * @memberof utils
 * @function
 * @description Returns a cookie value given its name
 * @param {string} name - The cookie's name
 * @param {string} path - The path associated with the cookie
 */
export const deleteCookie = (name, path) => {
  setCookie(name, '', -1, path);
};

/**
 * @memberof utils
 * @function
 * @description Loads a script from the campaign-cdn and appends it to
 * the body, resolving the promise when it loads. The optional second
 * paramater will check for an expected global before loading to avoid
 * including the same package.
 * @param {string} path - The path to the file, including the filename and
 * extension, within campaign-cdn.annalect.com/dependencies/
 * @param {string=} globalName - The key name that will be added to the
 * window object globally. This is used to check if it's already present.
 * @returns {Promise}
 */
export const loadScriptFromCDN = (path, globalName) =>
  new Promise((res, rej) => {
    // Don't reload the script, also make dev less annoying
    if (globalName?.trim() && window[globalName]) {
      res();
      return;
    }

    if (!path && typeof path !== 'string')
      rej(new TypeError(`Path parameter "${path}" is not a valid CDN path.`));

    const script = document.createElement('script');

    script.src = `${CDN_DEPENDENCIES_BASE}${path}`;
    script.addEventListener('load', res);
    script.addEventListener('error', rej);
    document.body.appendChild(script);
  });

/**
 * @memberof utils
 * @function
 * @description Loads a stylesheet from the campaign-cdn and appends it to
 * the document head, resolving the promise when it loads.
 * @param {string} path - The path to the file, including the filename and
 * extension, within campaign-cdn.annalect.com/dependencies/
 * @returns {Promise}
 */
export const loadStylesheetFromCDN = path =>
  new Promise((res, rej) => {
    if (!path && typeof path !== 'string')
      rej(new TypeError(`Path parameter "${path}" is not a valid CDN path.`));

    const style = document.createElement('link');

    style.rel = 'stylesheet';
    style.type = 'text/css';
    style.href = `${CDN_DEPENDENCIES_BASE}${path}`;
    style.addEventListener('load', res);
    style.addEventListener('error', rej);
    document.head.appendChild(style);
  });

export const downloadFile = async (source, defaultFilename = 'file') => {
  let blob;
  let filename = defaultFilename;
  // Support expanding YYYYMMDD in filename to current date
  if (filename.includes('YYYYMMDD')) {
    const now = new Date();
    const todayUTC = new Date(
      Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()),
    );
    const yyyymmdd = todayUTC.toISOString().slice(0, 10).replace(/-/g, '');
    filename = filename.replaceAll(/YYYYMMDD/g, yyyymmdd);
  }

  if (source instanceof Response) {
    blob = await source.blob();
    filename = source.headers.has('Content-Disposition')
      ? source.headers
          .get('Content-Disposition')
          .split('filename=')[1]
          .split(';')[0]
          .replace(/['"]/g, '')
      : defaultFilename;
  } else if (source instanceof Blob) {
    blob = source;
  } else if (typeof source === 'string') {
    blob = new Blob([source]);
  } else {
    throw Error(`Unsupported source type: ${typeof source}`);
  }

  // Replace problematic chars in filename with underscores
  filename = filename.replaceAll(/[^0-9a-zA-Z-._]/g, '_');

  const blobUrl = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a); // append element to the dom (required in firefox)
  a.click();
  a.remove(); // afterwards remove the element
};

export const downloadCSVFile = async (array2D, filename) => {
  const quote = val =>
    val?.match && val.match(/\n|,|"/)
      ? `"${val.replace(/"/g, '""')}"`
      : (val ?? '');

  const csv = array2D.map(row => row.map(quote).join(',')).join('\n');
  return downloadFile(csv, filename);
};

export const downloadElementAsImage = async (elem, filename = 'image.png') => {
  // Workaround for grid layout failing to export due to Chrome bug:
  // https://bugs.chromium.org/p/chromium/issues/detail?id=1305997
  // Eventually when Chrome (99-101?) is outdated, this code can be removed.
  // On the fly try to convert grid layouts to use absolute positioning (relative to the
  // container element) to export image. Afterwards revert CSS back to original.
  //
  // Also workaround for placeholder text isue. Temporarily change CSS as above.
  // https://github.com/bubkoo/html-to-image/issues/269

  const changes = [];
  const undoChanges = [];

  // Detect if the Chrome 99,100,101 grid bug is present
  async function testHasGridBug() {
    let isCorrect = true;
    const tempEl = document.createElement('div');
    tempEl.attachShadow({ mode: 'open' });
    tempEl.shadowRoot.innerHTML = `
      <div style="display: grid; grid-template: 'a b' 1fr 'c d' 1fr / 1fr 1fr; width: 2px; height: 2px">
        <div style="grid-area: a; background: #f00;"></div>
        <div style="grid-area: c; background: #0f0;"></div>
        <div style="grid-area: d; background: #00f;"></div>
        <div style="grid-area: b; background: #000;"></div>
      </div>
    `;
    try {
      document.body.append(tempEl);
      const pixels = await htmlToImage.toPixelData(
        tempEl.shadowRoot.firstElementChild,
      );
      const expecting = [
        255, 0, 0, 255, 0, 0, 0, 255, 0, 255, 0, 255, 0, 0, 255, 255,
      ];
      isCorrect =
        JSON.stringify(expecting) === JSON.stringify(Array.from(pixels));
    } catch {
      isCorrect = false;
    } finally {
      tempEl.remove();
    }
    return !isCorrect;
  }

  const hasGridBug = await testHasGridBug();

  // Update changes and undoChanges arrays with callbacks to perform to make a
  // particular css change. We postpone making any change until all changes are fully
  // planned out.
  function changeCSS(node, cssProp, value, priority = '') {
    const startValue = node.style.getPropertyValue(cssProp);
    const startPriority = node.style.getPropertyPriority(cssProp);
    if (value === undefined) {
      // Delete
      changes.push(() => node.style.removeProperty(cssProp));
    } else {
      // Set
      changes.push(() => node.style.setProperty(cssProp, value, priority));
      undoChanges.push(() => node.style.removeProperty(cssProp));
    }
    if (startValue !== '') {
      undoChanges.push(() =>
        node.style.setProperty(cssProp, startValue, startPriority),
      );
    }
  }

  // Recursively modify CSS to workaround issues with rendering. Currently this
  // addresses two issues (Chrome grid bug & problems with rendering placeholders)
  function fixCSS(node, immediateGridChild = false) {
    // If grid bug is present and node has css style: "display: grid"
    // Change display to block and recurse on children, using fixed positioning for
    // all immediate child elements. (Keep track of changes so we can undo after)
    const needsGridFix =
      hasGridBug &&
      node.computedStyleMap &&
      node.computedStyleMap().get('display').toString() === 'grid';
    if (needsGridFix) {
      const rect = node.getBoundingClientRect();
      changeCSS(node, 'display', 'block');
      changeCSS(node, 'position', 'relative');
      changeCSS(node, 'height', `${rect.height}px`);
      changeCSS(node, 'width', `${rect.width}px`);
      // Delete these
      changeCSS(node, 'grid', undefined);
      changeCSS(node, 'grid-template', undefined);
      changeCSS(node, 'grid-template-columns', undefined);
      changeCSS(node, 'grid-template-rows', undefined);
      changeCSS(node, 'grid-template-areas', undefined);
      changeCSS(node, 'grid-auto-rows', undefined);
      changeCSS(node, 'grid-auto-columns', undefined);
      changeCSS(node, 'grid-auto-flow', undefined);
    }

    // Use fixed positioning for immediate children of "display: grid" element
    if (immediateGridChild) {
      const parentRect = node.parentElement.getBoundingClientRect();
      const rect = node.getBoundingClientRect();
      changeCSS(node, 'top', `${rect.top - parentRect.top}px`);
      changeCSS(node, 'left', `${rect.left - parentRect.left}px`);
      changeCSS(node, 'height', `${rect.height}px`);
      changeCSS(node, 'width', `${rect.width}px`);
      changeCSS(node, 'position', 'absolute');
      // Delete these
      changeCSS(node, 'grid-area', undefined);
      changeCSS(node, 'grid-column', undefined);
      changeCSS(node, 'grid-row', undefined);
      changeCSS(node, 'grid-row-start', undefined);
      changeCSS(node, 'grid-row-end', undefined);
      changeCSS(node, 'grid-column-start', undefined);
      changeCSS(node, 'grid-column-end', undefined);
    }

    // Fix issue with placeholder text not getting styled
    if (['INPUT', 'TEXTAREA'].includes(node.tagName) && node.value === '') {
      changeCSS(node, 'color', 'rgba(54, 54, 54, 0.3)');
    }

    // Recurse on children
    Array.from(node.children).forEach(child => fixCSS(child, needsGridFix));
    if (node.shadowRoot) fixCSS(node.shadowRoot);
  }

  try {
    fixCSS(elem);
    changes.forEach(f => f());
    const blob = await htmlToImage.toBlob(elem);
    downloadFile(blob, filename);
  } finally {
    undoChanges.forEach(f => f());
  }
};

export const avoidBugHtml = (strings, ...values) => {
  if (!avoidBugHtml.lookup) avoidBugHtml.lookup = {};
  const { lookup } = avoidBugHtml;
  const key = strings.join('\0'); // Rely on uniqueness of template string vs callsite
  if (!lookup[key]) lookup[key] = strings;
  if (lookup[key] !== strings) {
    // console.warn('Browser bug detected with tagged template literal');
  }
  return html(lookup[key], ...values);
};

/**
 * Debounce given function so it does not run until wait milliseconds after the last call.
 * If immediate flag is true, then it will run on the first call and not run again until wait milliseconds
 * after the last call.
 *
 * Based off following sources:
 * https://davidwalsh.name/javascript-debounce-function
 * https://levelup.gitconnected.com/debounce-in-javascript-improve-your-applications-performance-5b01855e086
 *
 * @param func {function} function to debounce
 * @param delay {number} time to wait in milliseconds
 * @param immediate {boolean} True if function should be called on the leading edge instead of trailing edge
 * @return {function}
 */
export function debounce(func, delay, immediate = false) {
  let timeout;
  return (...args) => {
    const callNow = immediate && !timeout;
    const later = () => {
      timeout = null;
      if (!immediate) func(...args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, delay);
    if (callNow) func(...args);
  };
}

/**
 * Debounce given async function so it does not run until delay milliseconds after the last call.

 * @param func {function} function to debounce
 * @param delay {number} time to wait in milliseconds
 * @return {function}
 */
export function debounceAsync(func, delay) {
  let timeout;
  return function (...args) {
    // Create a new promise that will be returned by the debounced function
    return new Promise((resolve, reject) => {
      const later = async () => {
        clearTimeout(timeout);
        try {
          // Await the function and resolve the promise with its result
          const result = await func(...args);
          resolve(result);
        } catch (error) {
          // If the function throws an error, reject the promise
          reject(error);
        }
      };

      clearTimeout(timeout);
      timeout = setTimeout(later, delay);
    });
  };
}

export const trackPortalEvent = (event, eventType, accessPoint) => {
  const detail = {
    event,
    eventType,
    accessPoint,
  };
  const customEvent = new CustomEvent('track-portal-event', { detail });
  window.dispatchEvent(customEvent);
};
